import React from "react";
import { Link } from "react-router-dom";
import "./OfferNeonPanel.css";

/**
 * OfferNeonPanel
 * - Lighter, "draft/offer" tier neon panel to avoid overshadowing ManageContract.jsx
 * - Use in Contracts.jsx (offer stage)
 *
 * Props:
 *  - offer: {
 *      playerName,
 *      playerUsername,
 *      contractDuration,
 *      additionalNotes,
 *      playerProfileUrl,        // INTERNAL FRONTEND ROUTE (keep as-is, do NOT absolutize)
 *      contractPolicyUrl,       // POLICY DOC PATH/URL (we will NOT absolutize so it can live in React /public)
 *      uploadedContractUrl,     // file url/path (can absolutize)
 *      statusText,
 *      withdrawReason,
 *      submittedAt,
 *      badge: { title, subtitle }
 *    }
 *  - toAbsoluteUrl: (urlOrPath) => absoluteUrl (optional)   // use ONLY for uploaded files
 *  - onViewProfile: () => void (optional)                   // if provided, used for SPA navigation
 *  - actions: ReactNode (optional)                          // buttons (e.g., Withdraw)
 *  - footer: ReactNode (optional)                           // extra info (e.g., countdown/active message)
 */
export default function OfferNeonPanel({
    offer,
    toAbsoluteUrl,
    onViewProfile,
    actions,
    footer,
}) {
    // For UPLOADED FILES only (backend/static). Do NOT use this for internal routes.
    const absFile = (u) => {
        if (!u) return "#";
        return typeof toAbsoluteUrl === "function" ? toAbsoluteUrl(u) : u;
    };

    if (!offer) {
        return (
            <div className="offer-neon-wrap">
                <div className="offer-neon-card loading">
                    <div className="offer-neon-inner">
                        <div className="offer-neon-title-row">
                            <div className="offer-neon-line" />
                            <h3 className="offer-neon-title">LOADING OFFER...</h3>
                            <div className="offer-neon-line" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const {
        playerName,
        playerUsername,
        contractDuration,
        additionalNotes,
        playerProfileUrl,
        contractPolicyUrl,
        uploadedContractUrl,
        statusText,
        withdrawReason,
        submittedAt,
        badge,
    } = offer;

    const hasProfile = !!playerProfileUrl;

    // Only show Reason if it's a non-empty string (prevents "Reason: —")
    const reason = typeof withdrawReason === "string" ? withdrawReason.trim() : "";
    const hasReason = !!reason;

    return (
        <div className="offer-neon-wrap">
            <div className="offer-neon-card">
                {/* small corner accents (lighter than ManageContract) */}
                <span className="offer-corner tl" />
                <span className="offer-corner tr" />
                <span className="offer-corner bl" />
                <span className="offer-corner br" />

                <div className="offer-neon-inner">
                    {/* Title */}
                    <div className="offer-neon-title-row">
                        <div className="offer-neon-line" />
                        <h3 className="offer-neon-title">OFFER TO SIGN</h3>
                        <div className="offer-neon-line" />
                    </div>

                    <div className="offer-neon-body">
                        {/* Left column */}
                        <div className="offer-left">
                            <Row label="Offer to Sign" value={playerName || "Unknown"} highlight />

                            <Row
                                label="Player Profile"
                                value={
                                    hasProfile ? (
                                        // If parent provided SPA navigation, use it (preferred).
                                        typeof onViewProfile === "function" ? (
                                            <button
                                                type="button"
                                                className="offer-link-button"
                                                onClick={onViewProfile}
                                            >
                                                View Profile
                                            </button>
                                        ) : (
                                            // Fallback: still keep SPA navigation via React Router Link (NO hard reload).
                                            <Link to={playerProfileUrl} className="offer-link-strong">
                                                View Profile
                                            </Link>
                                        )
                                    ) : (
                                        "Not available"
                                    )
                                }
                            />

                            <Row label="Contract Duration" value={contractDuration || "—"} />

                            {additionalNotes ? <Row label="Additional Notes" value={additionalNotes} /> : null}

                            {uploadedContractUrl ? (
                                <Row
                                    label="View Uploaded Contract"
                                    value={
                                        <a
                                            href={absFile(uploadedContractUrl)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="offer-link-strong"
                                        >
                                            View Uploaded Contract
                                        </a>
                                    }
                                />
                            ) : null}

                            {contractPolicyUrl ? (
                                <div className="offer-policy">
                                    {/* ✅ IMPORTANT: do NOT absolutize policy URL
                      Put the doc in: frontend/public/VSN_Participant_Contract.docx
                      Then use: contractPolicyUrl="/VSN_Participant_Contract.docx"
                  */}
                                    <a href={contractPolicyUrl} target="_blank" rel="noreferrer">
                                        📄 Read VSN&apos;s official contract policy here
                                    </a>
                                </div>
                            ) : null}

                            {playerUsername ? (
                                <div className="offer-subtle">
                                    Username:{" "}
                                    <span className="offer-subtle-strong">{playerUsername}</span>
                                </div>
                            ) : null}

                            {submittedAt ? (
                                <div className="offer-subtle">
                                    Submitted At:{" "}
                                    <span className="offer-subtle-strong">
                                        {formatDateTime(submittedAt)}
                                    </span>
                                </div>
                            ) : null}

                            {/* ✅ Actions slot (Withdraw button, etc.) */}
                            {actions ? <div className="offer-actions">{actions}</div> : null}

                            {/* ✅ Footer slot (active message / countdown, etc.) */}
                            {footer ? <div className="offer-footer">{footer}</div> : null}
                        </div>

                        {/* Right column */}
                        <div className="offer-right">
                            <div className="offer-status-box">
                                <div className="offer-status-title">
                                    Offer Status:{" "}
                                    <span className="offer-status-title-strong">
                                        {statusText || "—"}
                                    </span>
                                </div>

                                {hasReason ? (
                                    <div className="offer-status-sub">
                                        Reason:{" "}
                                        <span className="offer-status-reason">{reason}</span>
                                    </div>
                                ) : null}
                            </div>

                            {/* optional badge */}
                            {badge?.title || badge?.subtitle ? (
                                <div className="offer-badge">
                                    {badge.subtitle ? <small>{badge.subtitle}</small> : null}
                                    {badge.title ? <strong>{badge.title}</strong> : null}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value, highlight }) {
    return (
        <div className="offer-row">
            <span className="offer-label">{label}:</span>
            <span className={highlight ? "offer-value highlight" : "offer-value"}>
                {value}
            </span>
        </div>
    );
}

function formatDateTime(input) {
    const d = new Date(input);
    if (Number.isNaN(d.getTime())) return String(input);
    return d.toLocaleString();
}